import { Award, Globe2, Shield, Zap, Users, Building } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import advancedManufacturing from "@/assets/advanced-manufacturing.jpg";
import globalLogistics from "@/assets/global-logistics.jpg";

const CompanyFeatures = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: Award,
      titleKey: "features.iso.title",
      descriptionKey: "features.iso.description"
    },
    {
      icon: Globe2,
      titleKey: "features.global.title",
      descriptionKey: "features.global.description"
    },
    {
      icon: Shield,
      titleKey: "features.compliance.title",
      descriptionKey: "features.compliance.description"
    },
    {
      icon: Zap,
      titleKey: "features.custom.title",
      descriptionKey: "features.custom.description"
    },
    {
      icon: Users,
      titleKey: "features.team.title",
      descriptionKey: "features.team.description"
    },
    {
      icon: Building,
      titleKey: "features.facilities.title",
      descriptionKey: "features.facilities.description"
    }
  ];

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-4">
            {t('features.title')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {t('features.subtitle')}
          </p>
        </div>

        {/* Feature Images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="relative aspect-video rounded-xl overflow-hidden shadow-elevated group">
            <img
              src={advancedManufacturing}
              alt="Advanced API manufacturing facility — Longshine Boi-Tech, Pune, India (WHO-GMP, ISO 9001:2015 certified)"
              width="800"
              height="600"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent flex items-end p-6">
              <div className="text-white">
                <h3 className="text-2xl font-bold mb-2">State-of-the-Art Facilities</h3>
                <p className="text-white/90">Advanced cGMP manufacturing on current-generation process equipment</p>
              </div>
            </div>
          </div>
          
          <div className="relative aspect-video rounded-xl overflow-hidden shadow-elevated group">
            <img
              src={globalLogistics}
              alt="Global pharmaceutical distribution network — Longshine Boi-Tech exports from Pune, India to 50+ countries"
              width="800"
              height="600"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent flex items-end p-6">
              <div className="text-white">
                <h3 className="text-2xl font-bold mb-2">Global Reach</h3>
                <p className="text-white/90">Serving 50+ countries with reliable logistics network</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div
                key={index}
                className="bg-card p-8 rounded-xl border border-border shadow-card hover:shadow-elevated transition-all duration-300 group"
              >
                <div className="mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                    <IconComponent className="w-8 h-8 text-primary group-hover:text-primary-foreground" />
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold text-card-foreground mb-3 group-hover:text-primary transition-colors">
                  {t(feature.titleKey)}
                </h3>
                
                <p className="text-muted-foreground leading-relaxed">
                  {t(feature.descriptionKey)}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CompanyFeatures;